import React, { useState, useEffect } from 'react';
import { apiService, User } from '../services/api';
import {
  Plus, Pencil, Trash2, Users, X, AlertTriangle,
} from 'lucide-react';
import PageLayout from './PageLayout';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [rolesAndScopes, setRolesAndScopes] = useState<{ roles: string[]; scopes: string[] }>({ roles: [], scopes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [formUsername, setFormUsername] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formFullName, setFormFullName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState('viewer');
  const [formScopes, setFormScopes] = useState<string[]>([]);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [userList, meta] = await Promise.all([
        apiService.getUsers(),
        apiService.getRolesAndScopes(),
      ]);
      setUsers(userList || []);
      setRolesAndScopes(meta || { roles: [], scopes: [] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormUsername('');
    setFormPassword('');
    setFormFullName('');
    setFormEmail('');
    setFormRole('viewer');
    setFormScopes([]);
    setFormError(null);
  };

  const openCreateModal = () => {
    resetForm();
    setCreateModalOpen(true);
    setEditModalOpen(false);
    setEditingUser(null);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormUsername(user.username);
    setFormPassword('');
    setFormFullName(user.full_name || '');
    setFormEmail(user.email || '');
    setFormRole(user.role || 'viewer');
    setFormScopes(user.scopes || []);
    setFormError(null);
    setEditModalOpen(true);
    setCreateModalOpen(false);
  };

  const closeModals = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setEditingUser(null);
    resetForm();
  };

  const toggleScope = (scope: string) => {
    setFormScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSaving(true);
    try {
      await apiService.createAppUser({
        username: formUsername.trim(),
        password: formPassword,
        full_name: formFullName.trim() || undefined,
        email: formEmail.trim() || undefined,
        role: formRole,
        scopes: formScopes,
      });
      setSuccessMsg('User created successfully');
      closeModals();
      loadData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setFormSaving(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setFormError(null);
    setFormSaving(true);
    try {
      await apiService.updateAppUser(editingUser.id, {
        full_name: formFullName.trim() || undefined,
        email: formEmail.trim() || undefined,
        role: formRole,
        scopes: formScopes,
        ...(formPassword ? { password: formPassword } : {}),
      });
      setSuccessMsg('User updated successfully');
      closeModals();
      loadData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      await apiService.deleteAppUser(userId);
      setSuccessMsg('User deactivated successfully');
      setDeleteConfirmId(null);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate user');
    }
  };

  const roles = rolesAndScopes.roles.length ? rolesAndScopes.roles : ['admin', 'operator', 'viewer'];
  const scopes = rolesAndScopes.scopes.length ? rolesAndScopes.scopes : [
    'devices', 'policies', 'app-deployment', 'remote-execution', 'patch-management',
    'inventory', 'compliance', 'license-management', 'analytics', 'settings', 'user-management',
  ];

  return (
    <PageLayout
      title="User Management"
      subtitle="Create users and assign roles and scopes."
      icon={<Users size={28} className="text-blue-600" />}
      action={
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          Add User
        </button>
      }
    >
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {successMsg && (
          <div className="mx-4 mt-4 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm">
            {successMsg}
          </div>
        )}
        {error && (
          <div className="mx-4 mt-4 flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertTriangle size={18} />
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading users...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Username</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Full Name</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Email</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Role</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Scopes</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={`border-b border-slate-100 ${!u.is_active ? 'bg-slate-50 opacity-70' : ''}`}>
                    <td className="px-6 py-3 font-medium text-slate-800">{u.username}</td>
                    <td className="px-6 py-3 text-slate-600">{u.full_name || '—'}</td>
                    <td className="px-6 py-3 text-slate-600">{u.email || '—'}</td>
                    <td className="px-6 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                        u.role === 'operator' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span className="text-slate-600 text-sm">
                        {(u.scopes || []).length > 0
                          ? (u.scopes || []).slice(0, 3).join(', ') + ((u.scopes || []).length > 3 ? '…' : '')
                          : u.role === 'admin' ? 'All' : '—'}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span className={u.is_active ? 'text-emerald-600' : 'text-slate-400'}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(u)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        {u.is_active && (
                          deleteConfirmId === u.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleDelete(u.id)}
                                className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteConfirmId(null)}
                                className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmId(u.id)}
                              className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Deactivate"
                            >
                              <Trash2 size={16} />
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="p-12 text-center text-slate-500">
                No users yet. Click &quot;Add User&quot; to create one.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(createModalOpen || editModalOpen) && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">
                {editModalOpen ? 'Edit User' : 'Create User'}
              </h3>
              <button type="button" onClick={closeModals} className="p-1 text-slate-500 hover:text-slate-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={editModalOpen ? handleUpdate : handleCreate} className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm">
                  <AlertTriangle size={16} />
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                <input
                  type="text"
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                  disabled={editModalOpen}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 disabled:bg-slate-100"
                  placeholder="jdoe"
                />
              </div>

              {!editModalOpen && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    required={!editModalOpen}
                    minLength={6}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800"
                    placeholder="••••••••"
                  />
                </div>
              )}

              {editModalOpen && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New Password (leave blank to keep)</label>
                  <input
                    type="password"
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    minLength={6}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800"
                    placeholder="••••••••"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={formFullName}
                  onChange={(e) => setFormFullName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <select
                  value={formRole}
                  onChange={(e) => setFormRole(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800"
                >
                  {roles.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Scopes (feature access)</label>
                <div className="flex flex-wrap gap-2">
                  {scopes.map((s) => (
                    <label key={s} className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formScopes.includes(s)}
                        onChange={() => toggleScope(s)}
                        className="rounded border-slate-300 text-blue-600"
                      />
                      <span className="text-sm text-slate-700">{s}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSaving || (!editModalOpen && (!formUsername.trim() || !formPassword))}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {formSaving ? 'Saving…' : editModalOpen ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default UserManagement;
